import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { Plus, Smartphone, UserCheck, UserX, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

const DevicePool = () => {
  const { user } = useContext(AuthContext);
  const [devices, setDevices] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [assignDialog, setAssignDialog] = useState({ open: false, device: null });
  const [openDays, setOpenDays] = useState(['Sunday']);
  const [formData, setFormData] = useState({
    serial_number: '',
    device_type: 'Zebra Scanner',
    store_id: '',
    home_store: ''
  });
  const token = localStorage.getItem('token');

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];

  // Day colors
  const dayColors = {
    Sunday: 'bg-purple-100 text-purple-700 border-purple-200',
    Monday: 'bg-blue-100 text-blue-700 border-blue-200',
    Tuesday: 'bg-teal-100 text-teal-700 border-teal-200',
    Wednesday: 'bg-amber-100 text-amber-700 border-amber-200',
    Thursday: 'bg-red-100 text-red-700 border-red-200'
  };

  // Roles that can delete devices
  const canDeleteDevice = ['network_admin', 'regional_manager', 'complex_manager', 'store_manager', 'stocktake_champion'].includes(user?.role);

  // Get the current store_id
  const getStoreId = () => {
    if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
      return localStorage.getItem('selectedStoreId') || user?.store_id;
    }
    return user?.store_id;
  };

  useEffect(() => {
    fetchData();
  }, [user?.store_id]);

  // Listen for store changes
  useEffect(() => {
    const handleStoreChanged = (event) => {
      fetchData();
    };
    
    window.addEventListener('storeChanged', handleStoreChanged);
    return () => window.removeEventListener('storeChanged', handleStoreChanged);
  }, []);

  const fetchData = async () => {
    try {
      const storeId = getStoreId();
      const storeParam = storeId ? `?store_id=${storeId}` : '';
      
      const [devicesRes, rostersRes, storesRes] = await Promise.all([
        fetch(`${API}/devices${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/rosters${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/stores`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDevices(await devicesRes.json());
      setRosters(await rostersRes.json());
      setStores(await storesRes.json());
    } catch (error) {
      console.error(error);
    }
  };

  // Get rostered members by day
  const getRostersByDay = (day) => {
    return rosters.filter(r => r.day === day);
  };

  const toggleDay = (day) => {
    setOpenDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success('Device added successfully');
        setOpen(false);
        setFormData({ serial_number: '', device_type: 'Zebra Scanner', store_id: '', home_store: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to add device');
    }
  };

  const handleAssign = async (roster) => {
    try {
      await fetch(`${API}/devices/${assignDialog.device.id}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          user_id: roster.user_id || roster.id,
          user_name: roster.user_name 
        })
      });
      toast.success(`Device assigned to ${roster.user_name}`);
      setAssignDialog({ open: false, device: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to assign device');
    }
  };

  const handleUnassign = async (deviceId) => {
    try {
      await fetch(`${API}/devices/${deviceId}/unassign`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Device unassigned');
      fetchData();
    } catch (error) {
      toast.error('Failed to unassign device');
    }
  };

  const handleDelete = async (device) => {
    if (!window.confirm(`Are you sure you want to delete device ${device.serial_number}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API}/devices/${device.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`Device ${device.serial_number} deleted`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete device');
      }
    } catch (error) {
      toast.error('Failed to delete device');
    }
  };

  return (
    <div className="space-y-8" data-testid="device-pool-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Device Pool</h1>
          <p className="text-slate-600">Manage Zebra scanners and device assignments</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-700 hover:bg-teal-800" data-testid="add-device-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Device
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-device-dialog">
            <DialogHeader>
              <DialogTitle>Add New Device</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="serial">Serial Number</Label>
                <Input
                  id="serial"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  placeholder="ZBR-2024-001"
                  required
                  data-testid="serial-input"
                />
              </div>
              <div>
                <Label htmlFor="type">Device Type</Label>
                <Input
                  id="type"
                  value={formData.device_type}
                  onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}
                  data-testid="type-input"
                />
              </div>
              <div>
                <Label htmlFor="home_store">Home Store (where device came from)</Label>
                <Input
                  id="home_store"
                  value={formData.home_store}
                  onChange={(e) => setFormData({ ...formData, home_store: e.target.value })}
                  placeholder="e.g., Melbourne CBD, Sydney SFS"
                  data-testid="home-store-input"
                />
              </div>
              <div>
                <Label htmlFor="store">Assign to Store</Label>
                <Select value={formData.store_id || "none"} onValueChange={(val) => setFormData({ ...formData, store_id: val === "none" ? "" : val })}>
                  <SelectTrigger data-testid="store-select">
                    <SelectValue placeholder="Select store (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No store assigned</SelectItem>
                    {stores.map((store) => (
                      <SelectItem key={store.id} value={store.id}>
                        {store.name} ({store.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-teal-700 hover:bg-teal-800" data-testid="submit-device-button">
                Add Device
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Assignment Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => setAssignDialog({ ...assignDialog, open })}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="assign-device-dialog">
          <DialogHeader>
            <DialogTitle>Assign Device</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600 mb-4">
              Assign <span className="font-semibold">{assignDialog.device?.serial_number}</span> to a rostered team member:
            </p>
            
            {rosters.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <p>No team members have been rostered yet.</p>
                <p className="text-sm mt-2">Add team members to the roster first.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {days.map((day) => {
                  const dayRosters = getRostersByDay(day);
                  const isOpen = openDays.includes(day);
                  
                  return (
                    <Collapsible
                      key={day}
                      open={isOpen}
                      onOpenChange={() => toggleDay(day)}
                      className="border rounded-lg overflow-hidden"
                    >
                      <CollapsibleTrigger className="w-full">
                        <div className={`flex items-center justify-between p-3 hover:bg-slate-50 ${dayColors[day]}`}>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{day}</span>
                            <span className="text-xs px-2 py-0.5 bg-white/50 rounded-full">
                              {dayRosters.length} member{dayRosters.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {isOpen ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="bg-white divide-y divide-slate-100">
                          {dayRosters.length === 0 ? (
                            <p className="p-3 text-sm text-slate-400 text-center">
                              No one rostered for {day}
                            </p>
                          ) : (
                            dayRosters.map((roster) => (
                              <button
                                key={roster.id}
                                className="w-full p-3 text-left hover:bg-slate-50 transition-colors flex items-center justify-between"
                                onClick={() => handleAssign(roster)}
                                data-testid={`assign-to-${roster.id}`}
                              >
                                <div>
                                  <div className="font-medium text-slate-900">{roster.user_name}</div>
                                  <div className="text-xs text-slate-500">
                                    {roster.team_member_number && `#${roster.team_member_number} • `}
                                    {roster.start_time} - {roster.end_time}
                                    {roster.home_store && ` • ${roster.home_store}`}
                                  </div>
                                </div>
                                <UserCheck className="w-4 h-4 text-teal-600" />
                              </button>
                            ))
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Devices Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {devices.map((device) => (
          <div
            key={device.id}
            className={`rounded-xl border-2 p-6 transition-all hover:shadow-md ${
              device.status === 'assigned'
                ? 'border-green-200 bg-green-50'
                : 'border-slate-200 bg-white'
            }`}
            data-testid={`device-card-${device.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-lg ${
                device.status === 'assigned' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
              }`}>
                <Smartphone className="w-6 h-6" />
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                  device.status === 'assigned'
                    ? 'bg-green-500 text-white'
                    : 'bg-slate-300 text-slate-700'
                }`}>
                  {device.status}
                </span>
                {canDeleteDevice && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(device)}
                    data-testid={`delete-device-${device.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">{device.serial_number}</h3>
            <p className="text-sm text-slate-600 mb-2">{device.device_type}</p>
            {device.home_store && (
              <p className="text-xs text-slate-500 mb-2">
                From: <span className="font-medium">{device.home_store}</span>
              </p>
            )}
            {device.assigned_to_name && (
              <div className="flex items-center gap-2 text-sm text-green-700 font-medium mb-4">
                <UserCheck className="w-4 h-4" />
                {device.assigned_to_name}
              </div>
            )}
            <div className="flex gap-2">
              {device.status === 'available' ? (
                <Button
                  size="sm"
                  className="flex-1 bg-teal-700 hover:bg-teal-800"
                  onClick={() => setAssignDialog({ open: true, device })}
                  data-testid={`assign-button-${device.id}`}
                >
                  Assign Device
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => handleUnassign(device.id)}
                  data-testid={`unassign-button-${device.id}`}
                >
                  <UserX className="w-4 h-4 mr-2" />
                  Unassign
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {devices.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <Smartphone className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">No devices in pool yet</p>
        </div>
      )}
    </div>
  );
};

export default DevicePool;
